import styled from '@emotion/styled';
import {TextField} from '@mui/material';

import {
  mixinCenterWrapper,
  mixinCursorPointer,
  mixinFlexboxColumn,
  mixinFocus,
  mixinFocusError,
  mixinInputDisabled,
  mixinInputError,
  mixinInputValid,
  mixinSVGIcon,
  mixinSVGIconMedium,
  mixinUnstyledButton,
} from '@ELib/styles/mixins';

import {ListboxComponent} from './ListboxComponent';

export const SideElement = styled.div`
  font-size: var(--font-size-tiny);
`;
export const MainElement = styled.div`
  white-space: pre-wrap;

  &.bold {
    font-weight: var(--font-weight-title);
  }
`;
export const TextElements = styled.div`
  ${mixinFlexboxColumn};
  overflow: hidden;
`;

export const ListItemWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: var(--gutter-little);

  .MuiCheckbox-root > svg {
    ${mixinSVGIconMedium};
  }

  svg {
    ${mixinSVGIcon}
  }

  &.category {
    padding-top: var(--gutter-little);

    &.first {
      padding-top: var(--gutter-small);
    }

    .category-main {
      color: var(--color-core-primary);
      font-weight: var(--font-weight-highlight);
    }
  }
`;

export const StyledTextField = styled(TextField)<{error?: boolean}>`
  box-sizing: border-box;
  width: 100%;

  .alignRight {
    text-align: right;
  }

  &.no-padding-right > .MuiInputBase-root {
    padding-right: 0;
  }

  .MuiInputBase-root {
    height: var(--input-min-height);
    ${({error}) => (error ? mixinInputError : mixinInputValid)};

    /** 
     * The focused styling needs to be applied manually here since the mixinInputError/mixinInputvalid
     * is not applied to the input directly, but instead a wrapper div which does not have the pseudoclass :focus
     */

    &.Mui-focused {
      ${({error}) => (error ? mixinFocusError : mixinFocus)};
    }

    &.Mui-disabled {
      ${mixinInputDisabled};
    }

    input {
      padding: 0;
    }

    .MuiOutlinedInput-notchedOutline {
      border: none;
    }
  }
`;

export const AdornmentWrapper = styled.button`
  ${mixinUnstyledButton};
  color: var(--color-core-dark-grey);

  ${mixinCenterWrapper};
  padding-left: var(--gutter-little);

  ${mixinCursorPointer}
  svg {
    ${mixinSVGIcon}
  }

  &.disabled {
    pointer-events: none;
  }
`;

export const FilterWrapper = styled.div`
  background-color: var(--color-core-grey-light);
  border-radius: 0 var(--button-border-radius) var(--button-border-radius) 0;
  margin-left: var(--gutter-small);
  border-left: solid 1px var(--color-core-grey);

  button {
    height: calc(var(--input-min-height) - 2 * var(--regular-border-width));
    padding: var(--gutter-small);
    border-radius: 0;
  }
`;

export const CenteredLi = styled.li`
  ${mixinCenterWrapper}
`;

export const StyledListbox = styled(ListboxComponent)`
  overflow: auto;
  max-height: var(--dropdown-max-height);
  background: var(--color-core-white);
  border: var(--regular-border-width) solid var(--color-menu-border-focus);
  list-style: none;
  padding: 0;
  margin: 0;

  &.alignRight .item {
    justify-content: flex-end;
  }

  li {
    color: var(--color-core-link);
    padding: var(--gutter-little) var(--gutter);
    font-weight: var(--dropdown-item-title);

    &.Mui-focused {
      background-color: var(--color-state-hover);
      color: var(--color-core-black);
      cursor: pointer;
    }

    &[aria-disabled='true'],
    &[aria-disabled='true']:hover,
    &.disabled {
      background-color: var(--color-core-white);
      color: var(--input-color-disabled);
      cursor: default;
      pointer-events: none;
    }

    &.filter-info {
      font-size: var(--font-size-small);
      font-weight: var(--label-subtext-font-weight);
      color: var(--color-core-black);
      background-color: var(--infobox-background-color);
    }
  }

  &.moreOptionPadding li {
    padding: var(--gutter-small) var(--gutter);
  }
`;

export const OptionEndAdornment = styled.div`
  margin-left: auto;
`;

export const EndAdornmentIconWrapper = styled.div`
  display: flex;
  margin-left: var(--gutter-little);

  svg {
    ${mixinSVGIcon};
  }

  :nth-of-type(n + 2) {
    margin-left: var(--gutter-tiny);
  }
`;
